/**
 * Les litiges, vus par l'écran — le calcul, hors de React.
 *
 * Même découpe que `ot.ts`, `emballage.ts` et `affretement.ts`, pour la même
 * raison : ce qui est ici se teste sans navigateur, sans base et sans session.
 *
 *
 * CE QUE CE FICHIER NE FAIT PAS
 * ---------------------------------------------------------------------------
 * **Il ne recompte rien.** Le nombre d'ouverts, le plus ancien, les ordres
 * bloqués et le montant qui attend viennent de l'API, comptés en SQL sur TOUS
 * les litiges ouverts — pas sur la page affichée. Les recalculer ici donnerait
 * le total de ce qui est à l'écran, ce qui n'est pas la même chose dès qu'un
 * filtre est posé, et deux chiffres du même nom finiraient par se contredire.
 *
 * **Il ne calcule pas non plus l'ancienneté.** `jours_ouvert` est rendu par la
 * base. Le refaire ici ferait diverger les deux d'un jour au passage de minuit,
 * selon le fuseau du navigateur.
 *
 * Ce fichier MET EN MOTS ce que l'API a compté, et décide de la couleur.
 */

import type { DecompteLitiges, Litige } from './api.js';

/**
 * LA PHRASE QUI FAIT L'ÉCRAN.
 *
 * « 4 litiges ouverts — le plus ancien depuis 73 jours · 3 ordres livrés
 * bloqués en facturation, 6 030,00 € HT »
 *
 * Sans la seconde moitié, la liste n'est qu'un pense-bête. Avec elle, c'est de
 * l'argent qui attend. C'est le pendant exact du « 5 dues sans bon » de
 * l'emballage : l'écran vaut par ce qu'il fait apparaître, pas par ce qu'il
 * liste.
 */
export function decompteLitiges(d: DecompteLitiges): string {
  if (d.ouverts === 0) {
    return 'Aucun litige ouvert.';
  }

  const combien = d.ouverts > 1 ? `${String(d.ouverts)} litiges ouverts` : '1 litige ouvert';

  const ancien =
    d.plus_ancien === null
      ? ''
      : d.plus_ancien === 0
        ? ' — le plus ancien ouvert aujourd’hui'
        : d.plus_ancien === 1
          ? ' — le plus ancien depuis 1 jour'
          : ` — le plus ancien depuis ${String(d.plus_ancien)} jours`;

  const montant = Number(d.montant);
  const bloques =
    d.bloques === 0 || !Number.isFinite(montant) || montant === 0
      ? ''
      : ` · ${d.bloques > 1 ? `${String(d.bloques)} ordres bloqués` : '1 ordre bloqué'} en facturation, ${new Intl.NumberFormat(
          'fr-FR',
          { style: 'currency', currency: 'EUR' },
        ).format(montant)} HT`;

  return `${combien}${ancien}${bloques}`;
}

/** Le délai moyen de résolution, en jours. `null` si aucun n'est résolu. */
export function delaiMoyen(litiges: readonly Litige[]): number | null {
  const delais = litiges
    .map((l) => l.jours_resolution)
    .filter((d): d is number => d !== null);
  if (delais.length === 0) return null;
  return Math.round(delais.reduce((t, d) => t + d, 0) / delais.length);
}

/**
 * L'ÂGE, EN JOURS, avec sa couleur.
 *
 * Le cadrage §7 : la couleur ne fait que répéter ce que le chiffre dit déjà, et
 * elle porte l'encre, jamais un aplat derrière le texte. Un daltonien lit
 * « 73 jours » et sait à quoi s'en tenir.
 *
 * Les deux seuils : une semaine, un mois. Ce ne sont pas des règles de gestion
 * — rien ne se déclenche à 8 jours — mais des repères de lecture : ce qui date
 * de la semaine est encore frais, ce qui dépasse le mois a été oublié.
 */
export type CouleurAge = 'sourdine' | 'attente' | 'refus';

export function ageDit(jours: number): { texte: string; couleur: CouleurAge } {
  const texte =
    jours === 0 ? 'aujourd’hui' : jours === 1 ? '1 jour' : `${String(jours)} jours`;
  const couleur: CouleurAge = jours > 30 ? 'refus' : jours > 7 ? 'attente' : 'sourdine';
  return { texte, couleur };
}

/** « Livraison 2 · Almacen Getafe » — où l'incident a eu lieu. */
export function etapeDite(l: Litige): string {
  const type = l.etape_type === 'chargement' ? 'Chargement' : 'Livraison';
  const ou = l.etape_site ?? l.etape_ville;
  return ou === null ? `${type} ${String(l.etape_rang)}` : `${type} ${String(l.etape_rang)} · ${ou}`;
}

/**
 * Ce litige bloque-t-il une facture ?
 *
 * Le cadrage de l'étape 10 : « blocage triple — litige ouvert, commande client
 * ou lettre de voiture manquante, accord tarifaire en attente ». Ici on ne
 * connaît que le premier des trois ; la facturation dira le reste.
 *
 * Un litige RÉSOLU ne bloque plus, quel que soit le statut de l'ordre.
 */
export function bloqueLaFacture(l: Litige): boolean {
  return l.resolu_le === null;
}
